import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart } from 'react-chartjs-2';
import { Box, Grid, IconButton, Slider, TextField, Typography, Tooltip } from '@mui/material';
import { SettingsBackupRestore as SettingsBackupRestoreIcon } from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, CategoryScale, BarElement, LinearScale, Tooltip as TooltipJS } from 'chart.js';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { WordCloudChart, WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import { useGetChartsQuery } from '../features/api/rest/apiChartsSlice';
import Spinner from './Spinner';
import Error from './Error';

function WordCloudComponent({ ean, attribute, searchParamsName = '' }) {
  ChartJS.register(WordCloudChart, WordCloudController, WordElement, ArcElement, CategoryScale, BarElement, LinearScale, TooltipJS);

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const componentSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const min = parseInt(searchParams.get(`${componentSearchParamsName}min`), 10) || null;
  const max = parseInt(searchParams.get(`${componentSearchParamsName}max`), 10) || null;
  const [option, setOption] = useState({});
  const [data, setData] = useState({});
  const [complete, setComplete] = useState(false);
  const [rangedFilter, setRangedFilter] = useState([0, 0]);
  const { tagcloud, isLoading, isFetching, isError } = useGetChartsQuery(
    { ean, attribute },
    {
      selectFromResult: (results) => ({
        ...results,
        tagcloud: results?.data,
      }),
    }
  );

  const calculateScalingFactor = (weightRatio) => {
    // The area of the WordCloud is represented by a sum of weights and must remain constant as the words entered vary
    // The sum of the weights is the sum of the weights of the words
    // The weight of a word represents the space it occupies in the WordCloud (word area)
    // The area of the word is obtained by: height of the letter multiplied by the base of the letter,
    // both multiplied by a scale factor and all multiplied by the average length of a word
    // From which, inverting the formula, we obtain the result below
    return (4000 * Math.sqrt(0.002)) / Math.sqrt(weightRatio);
  };

  useEffect(() => {
    if (tagcloud) {
      setRangedFilter([0, Math.max(...tagcloud.map((tag) => tag.weight))]);
    }
  }, [attribute]);

  useEffect(() => {
    if (!min && !max && tagcloud) {
      setRangedFilter([0, Math.max(...tagcloud.map((tag) => tag.weight))]);
    }
  }, [tagcloud]);

  useEffect(() => {
    searchParams.set(`${componentSearchParamsName}min`, rangedFilter[0]);
    searchParams.set(`${componentSearchParamsName}max`, rangedFilter[1]);
    navigate(`${pathname}?${searchParams.toString()}`);
  }, [rangedFilter]);

  useEffect(() => {
    if (tagcloud) {
      setOption({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label(model) {
                return tagcloud.filter((tag) => tag.word === model.label)[0].weight;
              },
            },
          },
        },
        color: 'black',
      });

      const totalWeight = tagcloud.filter((tag) => tag.weight >= rangedFilter[0] && tag.weight <= rangedFilter[1]).reduce((sum, item) => sum + item.weight, 0);

      setData({
        labels: tagcloud.map((tag) => tag.word),
        datasets: [
          {
            data: tagcloud.map((tag) => (tag.weight >= rangedFilter[0] && tag.weight <= rangedFilter[1] ? (tag.weight / totalWeight) * calculateScalingFactor(tag.weight / totalWeight) : 0)),
          },
        ],
      });
      setComplete(true);
    }
  }, [tagcloud, complete, rangedFilter]);

  const resetFilter = () => {
    setRangedFilter([0, Math.max(...tagcloud?.map((tag) => tag.weight))]);
    setComplete(false);
  };

  return (
    <Box sx={{ height: '100%' }} data-testid="WordCloudComponent">
      <Grid container display="flex" alignItems="center" flexWrap="no-wrap" columnGap={3} rowGap={2}>
        <Grid item flexShrink={1} flexBasis="64px">
          <TextField fullWidth value={rangedFilter[0]} label="MIN" id="minWeightRangedFilter" onChange={(e) => setRangedFilter([parseInt(e.target.value), rangedFilter[1]])} />
        </Grid>
        <Grid item flexGrow={1} flexBasis="64px">
          <Slider getAriaLabel={() => t('charts.wordcloud.filters.ariaLabel')} value={rangedFilter} onChange={(e) => setRangedFilter(e.target.value)} valueLabelDisplay="auto" step={1} />
        </Grid>
        <Grid item flexShrink={1} flexBasis="64px">
          <TextField fullWidth value={rangedFilter[1]} label="MAX" id="maxWeightRangedFilter" onChange={(e) => setRangedFilter([rangedFilter[0], parseInt(e.target.value)])} />
        </Grid>
        <Grid item marginLeft="auto" marginRight="auto">
          <Tooltip title={t('common.word.noFilter')} placement="left">
            <IconButton onClick={resetFilter} size="large">
              <SettingsBackupRestoreIcon data-testid="SettingsBackupRestoreIcon" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      {isLoading || isFetching || !complete ? (
        <Spinner />
      ) : isError ? (
        <Error />
      ) : data?.datasets[0].data.reduce((sum, item) => sum + item, 0) === 0 ? (
        <Typography variant="h6" sx={{ textAlign: 'center', mt: 5 }}>
          {t('charts.wordcloud.filters.noData')}
        </Typography>
      ) : (
        <Box sx={{ position: 'relative', height: '80%' }}>
          <Chart type="wordCloud" options={option} data={data} />
        </Box>
      )}
    </Box>
  );
}

export default WordCloudComponent;

WordCloudComponent.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  ean: PropTypes.string.isRequired,
  attribute: PropTypes.string.isRequired,
  searchParamsName: PropTypes.string,
};
